import type { Community, CommunityKind, NavigationItemId, UserWithCommunities } from '@/types';
import { AGGREGATOR_ADMIN_NAV_ITEMS } from '@/features/aggregator/aggregator-ui';
import { getEffectiveShellNavIds } from '@/lib/community-navigation';

export type CommunityShellKind = 'community' | 'aggregator';

export interface CommunityUiMode {
  shellKind: CommunityShellKind;
  kind: CommunityKind;
  headerBadge: 'aggregator' | null;
  showMembershipNav: boolean;
  pollMemberRequests: boolean;
  showCommunityProfileInMenu: boolean;
  showCommunitySettingsInMenu: boolean;
  showAdvertisementPopup: boolean;
}

export interface ShellAdminNavItem {
  id: string;
  label: string;
}

export function isAggregatorKind(kind: CommunityKind | string | null | undefined): boolean {
  return kind === 'AGGREGATOR';
}

export function getCommunityShellKind(
  kind: CommunityKind | string | null | undefined
): CommunityShellKind {
  return isAggregatorKind(kind) ? 'aggregator' : 'community';
}

export function getCommunityUiMode(
  community: Pick<Community, 'kind' | 'isMembershipManagementEnabled' | 'isAdvertisementEnabled'>
): CommunityUiMode {
  const shellKind = getCommunityShellKind(community.kind);
  const isAggregator = shellKind === 'aggregator';

  return {
    shellKind,
    kind: community.kind ?? 'COMMUNITY',
    headerBadge: isAggregator ? 'aggregator' : null,
    showMembershipNav: !isAggregator && Boolean(community.isMembershipManagementEnabled),
    pollMemberRequests: !isAggregator,
    showCommunityProfileInMenu: !isAggregator,
    showCommunitySettingsInMenu: !isAggregator,
    showAdvertisementPopup: !isAggregator && Boolean(community.isAdvertisementEnabled),
  };
}

export function getShellMainNavIds(
  community: Pick<
    Community,
    | 'kind'
    | 'navigationConfig'
    | 'isProjectsEnabled'
    | 'isBusinessDirectoryEnabled'
    | 'isGivesAsksEnabled'
    | 'isMembershipManagementEnabled'
  >,
  user: Pick<UserWithCommunities, 'id'> | null | undefined,
  uiMode: CommunityUiMode
): NavigationItemId[] {
  const navIds = getEffectiveShellNavIds(community, user);
  if (uiMode.showMembershipNav) {
    return [...navIds, 'membership' as NavigationItemId];
  }
  return navIds;
}

export function getShellAdminNavItems(
  community: Pick<
    Community,
    | 'kind'
    | 'isBusinessEnabled'
    | 'isLeadEnabled'
    | 'isThankYouNoteEnabled'
    | 'isTestimonialEnabled'
    | 'isMeetupEnabled'
    | 'isAdvertisementEnabled'
    | 'isMembershipManagementEnabled'
    | 'isPointsEnabled'
  >,
  isAdminOrCreator: boolean,
  uiMode: CommunityUiMode,
  permissions: readonly string[] = []
): ShellAdminNavItem[] {
  const canCreateSubcommunity = isAdminOrCreator || permissions.includes('create_subcommunity');
  const canManageNewMembers = isAdminOrCreator || permissions.includes('manage_new_members');

  if (uiMode.shellKind === 'aggregator') {
    return canCreateSubcommunity ? AGGREGATOR_ADMIN_NAV_ITEMS : [];
  }

  if (!isAdminOrCreator) {
    const items: ShellAdminNavItem[] = [];
    if (permissions.includes('manage_new_members')) {
      items.push({ id: 'requests', label: 'Member Requests' });
    }
    if (canCreateSubcommunity) {
      items.push({ id: 'sub-communities', label: 'Sub Community' });
    }
    return items;
  }

  const items: ShellAdminNavItem[] = [
    { id: 'settings', label: 'Community Settings' },
    { id: 'admin-members', label: 'Member Directory' },
    { id: 'sub-communities', label: 'Sub Community' },
    { id: 'informative-posts', label: 'Informative Posts' },
    { id: 'reported-posts', label: 'Reported Posts' },
  ];
  if (canManageNewMembers) items.splice(1, 0, { id: 'requests', label: 'Member Requests' });

  const isAnyBusinessPostEnabled =
    community.isLeadEnabled ||
    community.isThankYouNoteEnabled ||
    community.isTestimonialEnabled ||
    community.isMeetupEnabled;

  if (community.isBusinessEnabled && isAnyBusinessPostEnabled) {
    items.push({ id: 'business-report', label: 'Business Report' });
  }
  if (community.isPointsEnabled) {
    items.push({ id: 'points-dashboard', label: 'Points Dashboard' });
  }
  if (community.isAdvertisementEnabled) {
    items.push({ id: 'advertisement', label: 'Advertisements' });
  }
  if (community.isMembershipManagementEnabled) {
    items.push({ id: 'membership-admin', label: 'Membership Subscriptions' });
  }

  return items;
}
